import { createSelector } from 'reselect';

import { userInfoSelectors } from 'wanderu-ducks-account';
import { carrierDataSelectors } from 'wanderu-ducks-carriers';
import {
    liveCheckSelectors,
    liveCheckCachedTripAction,
} from 'wanderu-ducks-live-check';
import { tripSelectors } from 'wanderu-ducks-trips';
import {
    selectCurrency,
    selectLocale,
} from 'wanderu-i18n';
import { selectDetailedPricing, selectTripLineItems } from 'wanderu-orders';

import {
    submitFormAction,
    fetchTripBookingInfoAction,
    fetchFormAndSummaryAction,
} from '..';
import {
    SLIMMED_PROPERTIES,
    TRIP_LEG_ITEMS_FROM_QUOTE_DATA,
    USER_ACCOUNT_FIELD_IDS,
    USER_INFO_FIELD_NAME_MAP,
    READ_ONLY_FIELDS,
    ACCOUNT_PASSWORD_ID,
    INSURANCE_SELECTION_ID,
    MARKETING_OPT_IN_FIELD_ID,
    USER_EMAIL_FIELD_ID,
    LIVE_CHECK_CACHE_EXPIRED_ERROR,
    US_NON_STATE_TERRITORIES,
} from '../../constants';
import {
    checkTripNotFound,
    formatBookingForEvents,
    getFormField,
    getPriceListForTrip,
    getPriceListForTripForEvents,
    setAllFormFieldsAsModified,
} from '../../utils';
import SLICE_NAMESPACE from '../types';
import NAMESPACE from './types';
import { mapPollingStatus } from './utils';

function enrichFormWithCountries(form, countries) {
    const enrichedFields = form.fields.map((field) => {
        let enrichedField = { ...field };

        if (enrichedField.type === 'country' && enrichedField.values.length === 0) {
            enrichedField.values = countries;
        }

        if (!!enrichedField.fields && enrichedField.fields.length) {
            enrichedField = enrichFormWithCountries(enrichedField, countries);
        }

        return enrichedField;
    });

    return {
        ...form,
        fields: enrichedFields,
    };
}

function enrichFormWithStates(form, formCountry, countries) {
    const enrichedFields = form.fields.map((field) => {
        let enrichedField = { ...field };

        if (enrichedField.type === 'state') {
            const localityCountry = countries.find(country => country.id === formCountry);
            enrichedField.values = localityCountry?.states || [];
        }

        if (!!enrichedField.fields && enrichedField.fields.length) {
            enrichedField = enrichFormWithStates(enrichedField, formCountry, countries);
        }

        return enrichedField;
    });

    return {
        ...form,
        fields: enrichedFields,
    };
}

function setFieldValidation(field, validation) {
    if (!validation || validation.isValid === undefined) {
        return field;
    }

    return {
        ...field,
        errorMap: validation.errorMap,
        errorMessage: validation.errorMessage,
        isValid: validation.isValid,
    };
}

function enrichFormWithValidations(form, validations) {
    const enrichedFields = form.fields.map((field) => {
        const validation = getFormField(validations, field.id);

        let enrichedField = setFieldValidation(field, validation);

        if (enrichedField?.fields?.length && validation) {
            enrichedField = enrichFormWithValidations(enrichedField, validation);
        }

        return enrichedField;
    });

    return {
        ...form,
        fields: enrichedFields,
    };
}

function formatForm(formData, formCountry, localities, validations) {
    if (!formData) {
        return undefined;
    }

    let form = formData;

    if (localities.countries) {
        form = enrichFormWithCountries(form, localities.countries);

        if (formCountry) {
            form = enrichFormWithStates(form, formCountry, localities.countries);
        }
    }

    if (validations) {
        form = enrichFormWithValidations(form, validations);
    }

    return form;
}

function formatFormWithModifications(formData) {
    if (!formData) {
        return undefined;
    }

    return setAllFormFieldsAsModified(formData);
}

function selectData(state) {
    return state[SLICE_NAMESPACE][NAMESPACE];
}

function selectFormData(state) {
    return selectData(state).form;
}

function selectValidations(state) {
    return selectData(state).validations;
}

function selectDetails(state) {
    return selectData(state).details;
}

function selectPayment(state) {
    return selectData(state).payment;
}

function selectPaymentMethods(state) {
    return selectData(state).paymentMethods;
}

function selectLocalities(state) {
    return selectData(state).localities;
}

function compressForm(form) {
    return Object.keys(form).reduce((obj, key) => {
        if (key === 'fields') {
            return {
                ...obj,
                fields: form.fields.map(compressForm),
            };
        }

        if (SLIMMED_PROPERTIES.includes(key)) {
            return {
                ...obj,
                [key]: form[key],
            };
        }

        return obj;
    }, {});
}

function selectSlimFormData(state) {
    const form = selectFormData(state);

    if (!form) {
        return undefined;
    }

    return compressForm(form);
}

function selectFieldValue(state, path) {
    const form = selectFormData(state);

    if (!form || !getFormField(form, path)) {
        return undefined;
    }

    return getFormField(form, path).value;
}

function selectUserFormInfo(state) {
    return Object.entries(USER_ACCOUNT_FIELD_IDS).reduce((obj, [key, path]) => {
        const data = selectFieldValue(state, path);
        return data
            ? { ...obj, [key]: data }
            : obj;
    }, {});
}

function selectSeatInfo(state) {
    const {
        seats,
        wheelchairs,
    } = selectData(state);

    return { seats, wheelchairs };
}

function selectTripLiveCheck(state, tripId) {
    const {
        seats,
        wheelchairs,
        selectedFaresMap = {},
    } = selectBookingInfo(state);

    if (!tripId) {
        return undefined;
    }

    const lcParams = {
        seats,
        selectedFare: selectedFaresMap[tripId],
        tripId,
        wheelchairs,
    };
    return liveCheckSelectors.selectTripLiveCheck(state, lcParams);
}

function selectTripLiveChecks(state) {
    const { tripIds = [] } = selectBookingInfo(state);

    if (!tripIds.length) {
        return undefined;
    }

    return tripIds.map((tripId) => selectTripLiveCheck(state, tripId));
}

function selectBookingInfo(state) {
    return selectData(state);
}

function selectSeatingInfo(state) {
    const bookingInfo = selectBookingInfo(state) || {};

    return {
        seats: bookingInfo.seats,
        wheelChairs: bookingInfo.wheelchairs,
    };
}

const selectForm = createSelector(
    selectFormData,
    (state) => selectFieldValue(state, USER_ACCOUNT_FIELD_IDS.country),
    selectLocalities,
    selectValidations,
    formatForm,
);

const selectFormWithModifications = createSelector(
    selectFormData,
    formatFormWithModifications,
);

function selectQuoteSummary(state) {
    return selectData(state).quoteSummary;
}

function selectProcessingIdMap(state) {
    return selectData(state).processingIds;
}

function selectTravelInsurance(state) {
    const quoteSummary = selectQuoteSummary(state);

    if (!quoteSummary?.travelInsurance) {
        return undefined;
    }

    return quoteSummary.travelInsurance.map((product) => ({
        ...product,
        pricing: Object.keys(product.pricing).reduce((priceObj, priceId) => ({
            ...priceObj,
            [priceId]: Number(product.pricing[priceId]),
        }), {}),
    }));
}

function selectProcessingId(state, tripId) {
    return selectProcessingIdMap(state)[tripId];
}

function selectBookingStatusData(state, processingId) {
    return selectData(state).bookingStatus[processingId];
}

function selectBookingStatus(state, processingId) {
    const data = selectBookingStatusData(state, processingId);
    if (!data) {
        return undefined;
    }
    return mapPollingStatus(data);
}

function selectBookingStatuses(state) {
    const processingIdMap = selectProcessingIdMap(state);

    return Object.entries(processingIdMap).reduce((statuses, [tripId, processingId]) => {
        if (!selectBookingStatus(state, processingId)) {
            return statuses;
        }

        return {
            ...statuses,
            [tripId]: selectBookingStatus(state, processingId),
        };
    }, {});
}

function selectCarrierData(state) {
    const liveChecks = selectTripLiveChecks(state) || [];

    if (!liveChecks.filter(Boolean).length) {
        return {};
    }

    return liveChecks.reduce((obj, lc) => ({
        ...obj,
        [lc?.tripData?.trip_id]:
            carrierDataSelectors.selectCarrierData(state, lc?.tripData?.carrier),
    }), {});
}

function selectInsuranceProcessingId(state) {
    return selectData(state).insuranceProcessingId;
}

function selectInsuranceStatusData(state) {
    return selectData(state).insuranceStatus;
}

function selectInsuranceStatus(state) {
    const data = selectInsuranceStatusData(state);
    if (!data) {
        return undefined;
    }
    return mapPollingStatus(data);
}

function selectTripConfirmationMap(state) {
    return selectData(state).tripConfirmations;
}

function selectTripConfirmations(state) {
    const tripConfirmationMap = selectTripConfirmationMap(state);

    return Object.entries(tripConfirmationMap).map(([tripId, confirmation]) => ([
        tripId,
        confirmation,
    ]));
}

function getTripLegItemFromQuoteSummary({
    itemId, state, tripLeg, returnTrip = false,
}) {
    const quoteData = returnTrip
        ? selectReturnQuoteData(state)
        : selectDepartQuoteData(state);

    if (!quoteData || !quoteData[tripLeg.trip_id]) {
        return [];
    }

    return quoteData[tripLeg.trip_id][itemId];
}

function setTripLegData({ tripLeg, state, returnTrip }) {
    const leg = { ...tripLeg };
    const tripInformation = tripSelectors.selectTripInformation(state, leg.trip_id);
    Object.entries(TRIP_LEG_ITEMS_FROM_QUOTE_DATA)
        .forEach(([itemKey, itemId]) => {
            leg[itemKey] = getTripLegItemFromQuoteSummary({
                itemId,
                returnTrip,
                state,
                tripLeg: leg,
            });
        });

    if (tripInformation?.fareTitle) {
        leg.fareTitle = tripInformation.fareTitle;
    }

    return leg;
}

function selectDepartLegs(state) {
    const { departLegIds = [] } = selectBookingInfo(state);
    const departLegs = departLegIds.map(
        (tripId) => selectTripLiveCheck(state, tripId)?.tripData || [],
    );

    return departLegs.map((tripLeg) => setTripLegData({ state, tripLeg }));
}

function selectReturnLegs(state) {
    const { returnLegIds } = selectBookingInfo(state);
    if (!returnLegIds) {
        return undefined;
    }

    const returnLegs = returnLegIds.map(
        (tripId) => selectTripLiveCheck(state, tripId)?.tripData || [],
    );

    return returnLegs.map((tripLeg) => setTripLegData({ returnTrip: true, state, tripLeg }));
}

function mapQuoteData(quoteData, tripIds) {
    return quoteData?.trips.reduce((obj, data) => {
        const { id } = data;
        if (!tripIds.includes(id)) {
            return obj;
        }

        return { ...obj, [id]: data };
    }, {});
}

function selectDepartQuoteData(state) {
    const { departLegIds } = selectBookingInfo(state);
    const quoteData = selectQuoteSummary(state);

    return mapQuoteData(quoteData, departLegIds);
}

function selectReturnQuoteData(state) {
    const { returnLegIds } = selectBookingInfo(state);
    if (!returnLegIds) {
        return undefined;
    }

    const quoteData = selectQuoteSummary(state);

    return mapQuoteData(quoteData, returnLegIds);
}

function selectInsuranceSelected(state) {
    return selectData(state).insuranceSelected;
}

function selectUserInfo(state) {
    const isLoggedIn = userInfoSelectors.selectUserLoggedIn(state);

    if (!isLoggedIn) {
        return undefined;
    }

    const userInfo = userInfoSelectors.selectUserInfo(state);
    const flattenedUserInfo = {
        ...userInfo,
        ...userInfo?.billingAddress,
    };

    const formPathInfo = Object.keys(USER_ACCOUNT_FIELD_IDS).reduce((obj, name) => {
        const value = USER_ACCOUNT_FIELD_IDS[name];

        const key = USER_INFO_FIELD_NAME_MAP[name];

        return {
            ...obj,
            [value]: flattenedUserInfo[key],
        };
    }, {});

    return {
        ...formPathInfo,
    };
}

function selectFieldsByValidity({ fields, key = 'id', valid = true }) {
    return fields.reduce((validMap, field) => {
        if (field.fields) {
            return [
                ...validMap,
                ...selectFieldsByValidity({ fields: field.fields, key, valid }),
            ];
        }

        if (field.isValid !== undefined && field.isValid === valid) {
            return [
                ...validMap,
                field[key],
            ];
        }

        return validMap;
    }, []);
}

function selectValidFields(state) {
    const form = selectForm(state);

    if (!form) {
        return undefined;
    }

    return selectFieldsByValidity({ fields: form.fields });
}

function selectInvalidFields(state) {
    const form = selectForm(state);

    if (!form) {
        return undefined;
    }

    return selectFieldsByValidity({ fields: form.fields, valid: false });
}

const selectInvalidLabels = createSelector(
    selectForm,
    (form) => {
        if (!form) {
            return undefined;
        }

        return selectFieldsByValidity({ fields: form.fields, key: 'label', valid: false });
    },
);

function isReadOnlyField(field) {
    return !!READ_ONLY_FIELDS.includes(field.type);
}

function fieldIsValid(field, insuranceVisible) {
    const isHiddenInsuranceField = field.id === INSURANCE_SELECTION_ID && !insuranceVisible;

    return isHiddenInsuranceField
        || (field.isValid !== undefined && !!field.isValid)
        || isReadOnlyField(field);
}

function selectFieldsAreValid(fields, insuranceVisible) {
    return fields.every((field) => {
        if (field.fields) {
            return selectFieldsAreValid(field.fields, insuranceVisible);
        }

        return fieldIsValid(field, insuranceVisible);
    });
}

function selectAllFieldsAreValid(state) {
    const form = selectForm(state);
    const insuranceVisible = selectInsuranceVisible(state);

    if (!form) {
        return undefined;
    }

    return selectFieldsAreValid(form.fields, insuranceVisible);
}

function selectAllPurchasesSuccess(state) {
    const bookingSuccess = submitFormAction.selectSuccess(state);
    const insuranceSelected = selectInsuranceSelected(state);
    const insuranceStatus = selectInsuranceStatus(state);

    let allPurchasesSuccessList = [bookingSuccess];

    if (insuranceSelected) {
        allPurchasesSuccessList.push(insuranceStatus?.status === 'COMPLETED');
    }

    return allPurchasesSuccessList.every((status) => status === true);
}

/**
 * PLAT-198: Remove this selector and the ?? fallback in
 * selectPaymentPriceList once detailedPricing is proven stable and
 * quoteSummary is fully deprecated. See memory/quotesummary-deprecation-plan.md.
 *
 * Legacy price list derived from trip leg pricing and insurance products.
 * Used internally as the fallback input to selectPaymentPriceList when
 * detailedPricing is unavailable. Not exported — consumers should use
 * selectPaymentPriceList.
 */
const selectPriceList = createSelector(
    selectForm,
    selectDepartLegs,
    selectReturnLegs,
    selectInsuranceSelected,
    selectTravelInsurance,
    (form, departLegs, returnLegs, isInsuranceSelected, travelInsurance) => {
        if (!form) {
            return [];
        }
        const departTrip = {
            legs: departLegs,
        };
        const returnTrip = {
            legs: returnLegs,
        };
        let priceList = getPriceListForTrip({ departTrip, returnTrip });

        if (isInsuranceSelected) {
            priceList = [
                ...priceList,
                ...travelInsurance.map((product) => product?.pricing),
            ];
        }

        return priceList;
    },
);

const selectPriceListForEvents = createSelector(
    selectForm,
    selectDepartLegs,
    selectReturnLegs,
    selectInsuranceSelected,
    selectTravelInsurance,
    (form, departLegs, returnLegs, isInsuranceSelected, travelInsurance) => {
        if (!form) {
            return [];
        }
        const departTrip = {
            legs: departLegs,
        };
        const returnTrip = {
            legs: returnLegs,
        };
        let priceList = getPriceListForTripForEvents({ departTrip, returnTrip });

        if (isInsuranceSelected) {
            priceList = [
                ...priceList,
                {
                    insurance: travelInsurance.map((product) => product?.pricing),
                },
            ];
        }

        return priceList;
    },
);

const selectTripEventProperties = createSelector(
    [
        (state) => selectBookingInfo(state),
        (state) => selectDepartLegs(state),
        (state) => selectReturnLegs(state),
        (state, props) => props.itineraryId,
        (state, props) => props.orderId,
    ],
    (bookingInfo, departTrips, returnTrips, itineraryId, orderId) => formatBookingForEvents({
        bookingSessionId: bookingInfo.bookingSessionId,
        departTrips,
        itineraryId,
        orderId,
        returnTrips: returnTrips || [],
        seats: bookingInfo.seats,
    }),
);

function selectUserAccountExists(state) {
    return selectData(state).userAccountExists;
}

function selectUserEmail(state) {
    return selectData(state).userEmail;
}

function selectUserEmailValidHelper(fields) {
    return fields.reduce((emailField, field) => {
        if (Object.keys(emailField).length) {
            return emailField;
        }

        if (field.fields) {
            return selectUserEmailValidHelper(field.fields);
        }

        if (field.id === USER_EMAIL_FIELD_ID) {
            return field;
        }

        return emailField;
    }, {});
}

function selectUserEmailValid(state) {
    const form = selectForm(state);

    if (!form) {
        return undefined;
    }

    return !!selectUserEmailValidHelper(form.fields).isValid;
}

function selectPasswordFieldHelper(form) {
    return form.fields.reduce((passwordField, field) => {
        if (Object.keys(passwordField).length) {
            return passwordField;
        }

        if (field.fields) {
            return selectPasswordFieldHelper(field);
        }

        if (field.id === ACCOUNT_PASSWORD_ID) {
            return field;
        }

        return passwordField;
    }, {});
}

function selectPasswordField(state) {
    return selectPasswordFieldHelper(selectForm(state));
}

function selectInsuranceVisible(state) {
    const insuranceSupportMap = {
        CAD: ['CA', 'Canada'],
        USD: ['US', 'United States'],
    };

    const currency = selectCurrency(state);
    let billingCountry = selectFieldValue(state, USER_ACCOUNT_FIELD_IDS.country);
    const stateProvince = selectFieldValue(state, USER_ACCOUNT_FIELD_IDS.stateProvince);

    if (!billingCountry) {
        const locale = selectLocale(state);
        const [, location] = locale.split('-');

        billingCountry = location.toUpperCase();
    }

    return !!insuranceSupportMap[currency]?.includes(billingCountry)
        && !US_NON_STATE_TERRITORIES.includes(stateProvince);
}

function selectItineraryId(state) {
    return selectData(state).itineraryId;
}

function selectMarketingOptInHelper(form) {
    return form.fields.reduce((marketingField, field) => {
        if (Object.keys(marketingField).length) {
            return marketingField;
        }

        if (field.fields) {
            return selectMarketingOptInHelper(field);
        }

        if (field.id === MARKETING_OPT_IN_FIELD_ID) {
            return field;
        }

        return marketingField;
    }, {});
}

function selectMarketingOptIn(state) {
    return !!selectMarketingOptInHelper(selectForm(state)).value;
}

function selectEarliestExpiry(state, tokenMap) {
    return Object.entries(tokenMap)
        .reduce((earliestExpiresAt, [tripId, searchToken]) => {
            const expiresAt = liveCheckCachedTripAction.selectExpiresAt(
                state,
                `${tripId}-${searchToken}`,
            );

            if (typeof earliestExpiresAt === 'undefined' || expiresAt < earliestExpiresAt) {
                return expiresAt;
            }

            return earliestExpiresAt;
        }, undefined);
}

const selectLivecheckTripNotFound = createSelector(
    (state) => fetchTripBookingInfoAction.selectError(state),
    (error) => checkTripNotFound(error),
);

const selectFormFetchTripNotFound = createSelector(
    (state) => fetchFormAndSummaryAction.selectError(state),
    (error) => checkTripNotFound(error),
);

const selectLivecheckExpired = createSelector(
    (state) => fetchTripBookingInfoAction.selectError(state),
    (error) => error?.code === LIVE_CHECK_CACHE_EXPIRED_ERROR,
);

function selectGoogleDepartId(state) {
    return selectData(state).googlePlaceIds?.depart;
}

function selectGoogleDestinationId(state) {
    return selectData(state).googlePlaceIds?.destination;
}

/**
 * Transforms a detailedPricing lineItem (lineItemType === 'trip') into the unified TripLegPricing shape.
 * Base fare from the breakdown entry with lineItemBreakdownType === 'carrier_product', defaulting to 0
 * if absent. All monetary values sourced from displayTotals (already in user's display currency —
 * no frontend conversion needed). Filters out carrier_product (already in baseFare)
 * and wanderu_discount (carried in discountTotal instead).
 * Fee labels are set to the raw name; resolved via i18n t() in the component.
 *
 * @internal — exported for testing only; do not call directly in components.
 * @param {import('wanderu-orders').LineItem} lineItem
 * @returns {import('../../components/Summary/types').TripLegPricing}
 */
function transformLineItemToLegPricing(lineItem) {
    const tripBreakdown = lineItem.lineItemBreakdown.find((b) => b.lineItemBreakdownType === 'carrier_product');
    return {
        baseFare: tripBreakdown?.displayAmount ?? 0,
        currency: lineItem.displayTotals.currency,
        discountTotal: lineItem.displayTotals.discountTotal,
        fees: lineItem.lineItemBreakdown
            .filter((b) => b.lineItemBreakdownType !== 'carrier_product'
                && b.lineItemBreakdownType !== 'wanderu_discount')
            .map((b) => ({
                amount: b.displayAmount,
                id: b.name,
                label: b.name,
                type: b.lineItemBreakdownType,
            })),
        total: lineItem.displayTotals.total,
    };
}

/**
 * Transforms a quoteSummary leg into the unified TripLegPricing shape (fallback path).
 * Base fare from leg.pricing, fees from leg.fees, total computed as base + sum of fees.
 * Fee labels are already resolved by the API — t() defaultValue passes them through.
 *
 * @internal — exported for testing only; do not call directly in components.
 * @param {{ pricing: Record<string, { price: number }>, fees?: Array<{ id: string, label: string, pricing: Record<string, number>, type: string }> }} leg
 * @returns {import('../../components/Summary/types').TripLegPricing}
 */
function transformLegacyLegToLegPricing(leg) {
    // quoteSummary pricing is always single-currency (API pre-resolves to booking currency).
    const currency = leg.pricing
        ? Object.keys(leg.pricing)[0] ?? ''
        : '';
    const baseFare = leg.pricing?.[currency]?.price ?? 0;

    return {
        baseFare,
        currency,
        discountTotal: 0,
        fees: (leg.fees || []).map((fee) => ({
            amount: fee.pricing[currency] ?? 0,
            id: fee.id,
            label: fee.label,
            type: fee.type,
        })),
        total: baseFare
            + (leg.fees || []).reduce((sum, fee) => sum + (fee.pricing[currency] || 0), 0),
    };
}

function transformLineItemsToPriceList(lineItems) {
    return lineItems.map((item) => ({
        [item.displayTotals.currency]: item.displayTotals.total,
    }));
}

/**
 * Converts detailedPricing line items into the legacy { currency: total }[]
 * shape consumed by i18nMoney and payment calculations.
 * Returns null when detailedPricing is not yet loaded (idle/pending/failed).
 * The lineItems guard is defense-in-depth against malformed API responses.
 */
const selectDetailedPriceList = createSelector(
    selectDetailedPricing,
    (data) => {
        if (!data?.lineItems) return null;
        return transformLineItemsToPriceList(data.lineItems);
    },
);

/**
 * Canonical pricing selector for payment and display consumers.
 * Prefers detailedPricing line items (from the orders API) when available;
 * falls back to the legacy trip-leg-derived price list otherwise.
 *
 * null/undefined lineItems → falls back to legacy.
 * Empty lineItems array → returns [] (no fallback), because the API
 * only returns empty lineItems when the order total is genuinely $0.
 *
 * @returns {Array<Record<string, number>>} price list entries
 */
const selectPaymentPriceList = createSelector(
    selectDetailedPriceList,
    selectPriceList,
    (detailed, legacy) => detailed ?? legacy,
);

/**
 * Pure result function for selectDepartLegsWithPricing.
 * Exported for testing (the reselect mock in tests strips resultFunc).
 * Matches each leg to its detailedPricing lineItem by sku (leg.trip_id === lineItem.sku).
 * Falls back to quoteSummary-derived pricing for any leg with no matching lineItem.
 */
function buildDepartLegsWithPricing(legs, tripLineItems) {
    if (!legs) return undefined;
    return legs.map((leg) => {
        const lineItem = tripLineItems?.find((li) => li.sku === leg.trip_id);
        return {
            ...leg,
            legPricing: lineItem
                ? transformLineItemToLegPricing(lineItem)
                : transformLegacyLegToLegPricing(leg),
        };
    });
}

/**
 * Pure result function for selectReturnLegsWithPricing.
 * Exported for testing (the reselect mock in tests strips resultFunc).
 * Matches each leg to its detailedPricing lineItem by sku (leg.trip_id === lineItem.sku).
 * Falls back to quoteSummary-derived pricing for any leg with no matching lineItem.
 * Returns undefined for one-way trips.
 */
function buildReturnLegsWithPricing(legs, tripLineItems) {
    if (!legs) return undefined;
    return legs.map((leg) => {
        const lineItem = tripLineItems?.find((li) => li.sku === leg.trip_id);
        return {
            ...leg,
            legPricing: lineItem
                ? transformLineItemToLegPricing(lineItem)
                : transformLegacyLegToLegPricing(leg),
        };
    });
}

// PLAT-198 PR 5: wire components to use this selector instead of selectDepartLegs.
const selectDepartLegsWithPricing = createSelector(
    selectDepartLegs,
    selectTripLineItems,
    buildDepartLegsWithPricing,
);

// PLAT-198 PR 5: wire components to use this selector instead of selectReturnLegs.
// Returns undefined for one-way trips (no returnLegIds).
const selectReturnLegsWithPricing = createSelector(
    selectReturnLegs,
    selectTripLineItems,
    buildReturnLegsWithPricing,
);

export {
    buildDepartLegsWithPricing,
    buildReturnLegsWithPricing,
    selectAllFieldsAreValid,
    selectAllPurchasesSuccess,
    selectBookingInfo,
    selectBookingStatus,
    selectBookingStatuses,
    selectCarrierData,
    selectDepartLegs,
    selectDepartLegsWithPricing,
    selectDepartQuoteData,
    selectDetails,
    selectEarliestExpiry,
    selectFieldValue,
    selectFieldsAreValid,
    selectFieldsByValidity,
    selectForm,
    selectFormData,
    selectFormFetchTripNotFound,
    selectFormWithModifications,
    selectGoogleDepartId,
    selectGoogleDestinationId,
    selectInsuranceProcessingId,
    selectInsuranceSelected,
    selectInsuranceStatus,
    selectInsuranceVisible,
    selectInvalidFields,
    selectInvalidLabels,
    selectItineraryId,
    selectLivecheckExpired,
    selectLivecheckTripNotFound,
    selectMarketingOptIn,
    selectPasswordField,
    selectPayment,
    selectPaymentMethods,
    selectPaymentPriceList,
    selectPriceListForEvents,
    selectProcessingId,
    selectQuoteSummary,
    selectReturnLegs,
    selectReturnLegsWithPricing,
    selectReturnQuoteData,
    selectSeatInfo,
    selectSeatingInfo,
    selectSlimFormData,
    selectTravelInsurance,
    selectTripConfirmationMap,
    selectTripConfirmations,
    selectTripEventProperties,
    selectTripLiveCheck,
    selectTripLiveChecks,
    selectUserAccountExists,
    selectUserEmail,
    selectUserEmailValid,
    selectUserFormInfo,
    selectUserInfo,
    selectValidFields,
    transformLegacyLegToLegPricing,
    transformLineItemToLegPricing,
    transformLineItemsToPriceList,
};
